/**
 * SiteMobileMenu — menu burger de la nav publique.
 *
 * Pourquoi il existe : la barre publique MASQUE ses liens sous 820px
 * (`.public-header-v2 .header-nav { display: none }`). Sous ce seuil,
 * plus aucune entrée de nav n'était atteignable — ni Tarifs, ni le
 * reste. D'où ce tiroir.
 *
 * Il rend la MÊME liste que la barre, dans le même ordre, plus les
 * actions d'auth, et ne s'affiche qu'en dessous du point de rupture.
 * Au-dessus, il est absent du DOM : la barre garde exactement le rendu
 * qu'elle avait.
 *
 * Les entrées et la copie d'auth arrivent en props DEPUIS le header, donc
 * déjà traduites. Avant l'unification elles étaient lues ici dans la liste
 * française figée : le tiroir restait en français même en NL et en EN.
 *
 * SÉLECTEUR DE LANGUE. header-v2.css masque `.lang-switch` sous 820px
 * (« hide nav + lang on small screens ») et ce tiroir ne l'avait jamais
 * repris : sous ce seuil, aucune page publique n'offrait le moindre moyen
 * de changer de langue. Il est donc rendu ici dès que le parent fournit
 * `onLanguageChange` — ce que le header unique fait toujours, sur toutes
 * les pages publiques.
 *
 * Classes dédiées `sv2-menu-lang*` et non `.lang-switch` : cette dernière
 * est masquée par header-v2.css sous 820px, et le tiroir vit à l'intérieur
 * de `.public-header-v2` — la réutiliser l'aurait rendue invisible ici
 * aussi.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SITE_NAV, ROUTE_LOGIN, ROUTE_DASHBOARD } from './routes';
import useStartRoute from '../../hooks/useStartRoute';
import './siteV2.css';

// Même seuil que les deux feuilles de style qui masquent les liens.
// Une valeur différente laisserait soit un trou (aucune nav), soit un
// doublon (liens + burger côte à côte).
const BREAKPOINT = 820;

const LANG_OPTIONS = ['FR', 'NL', 'EN'];

// Copie d'auth par défaut. Le header la fournit traduite ; ce repli sert
// un appelant qui ne passerait rien.
const DEFAULT_COPY = {
  signIn: 'Se connecter',
  cta: 'Commencer gratuitement',
  myDashboard: 'Mon bureau',
};

export default function SiteMobileMenu({ language, onLanguageChange, nav, copy }) {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  // Le CTA du tiroir mène là où mènent tous les autres : cf.
  // hooks/useStartRoute.js.
  const startRoute = useStartRoute();
  const [open, setOpen] = useState(false);
  // `false` au premier rendu, TOUJOURS, puis mesuré dans l'effet : les
  // pages publiques sont pré-rendues sur un écran large et hydratées sur
  // le vrai ; lire `window.innerWidth` dans l'initialiseur rendait un
  // tiroir sur mobile là où le HTML n'en avait pas, et React jetait tout
  // le DOM pré-rendu pour repeindre. Le burger apparaît au premier effet,
  // une frame plus tard, sans désaccord d'hydratation.
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const onResize = () => {
      const isCompact = window.innerWidth <= BREAKPOINT;
      setCompact(isCompact);
      // Repasser en grand écran doit refermer le tiroir, sinon le voile
      // resterait posé sur une nav déjà entièrement visible.
      if (!isCompact) setOpen(false);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Échap referme, comme toute surface modale.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const go = useCallback((to) => {
    setOpen(false);
    navigate(to);
  }, [navigate]);

  if (!compact) return null;

  // Les entrées viennent du header (donc traduites) ; à défaut, la liste
  // française partagée. Même source que la barre : le tiroir ne peut pas
  // diverger d'elle.
  const items = Array.isArray(nav) && nav.length ? nav : SITE_NAV;
  const c = { ...DEFAULT_COPY, ...(copy || {}) };

  return (
    <>
      <button
        type="button"
        className="sv2-burger"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={open}
        data-testid="site-mobile-menu-toggle"
      >
        {open ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="sv2-menu-scrim"
            aria-label="Fermer le menu"
            onClick={() => setOpen(false)}
            data-testid="site-mobile-menu-scrim"
          />
          <div className="sv2-menu-panel" data-testid="site-mobile-menu">
            {items.map(({ label, to }) => (
              <a
                key={to}
                className="sv2-menu-link"
                href={to}
                onClick={(e) => { e.preventDefault(); go(to); }}
                data-testid={`site-mobile-menu-link-${to.replace(/\//g, '') || 'home'}`}
              >
                {label}
              </a>
            ))}

            {typeof onLanguageChange === 'function' && (
              <>
                <div className="sv2-menu-sep" />
                <div className="sv2-menu-lang" role="tablist" aria-label="Langue">
                  {LANG_OPTIONS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      role="tab"
                      aria-selected={String(language || '').toUpperCase() === l}
                      className={`sv2-menu-lang-opt${String(language || '').toUpperCase() === l ? ' active' : ''}`}
                      onClick={() => onLanguageChange(l)}
                      data-testid={`site-mobile-menu-lang-${l.toLowerCase()}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="sv2-menu-sep" />

            {authLoading ? null : user ? (
              <button type="button" className="sv2-menu-cta" onClick={() => go(ROUTE_DASHBOARD)}>
                {c.myDashboard}
              </button>
            ) : (
              <>
                <a
                  className="sv2-menu-link"
                  href={ROUTE_LOGIN}
                  onClick={(e) => { e.preventDefault(); go(ROUTE_LOGIN); }}
                >
                  {c.signIn}
                </a>
                <button type="button" className="sv2-menu-cta" onClick={() => go(startRoute)}>
                  {c.cta}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
